import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, onSnapshot } from 'firebase/firestore';
import { AlertTriangle, ArrowLeft, FileText, Globe, LayoutGrid, Link as LinkIcon, Loader2, Lock, RefreshCcw, Shapes, Upload, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../lib/firebase';
import { createApiError, getFriendlyErrorMessage } from '../lib/usageLimitMessages';
import {
  addSourceFileMetadata,
  addSourceUrlMetadata,
  buildSourceSetStoragePath,
  getSourceFileType,
  getSourceSet,
  listSourceSetFiles,
  validateSourceSetFiles,
  validateSourceSetUrl,
  attachGenerationJobToSourceSet,
  updateSourceSetSettings,
  updateSourceSetStatus
} from '../services/sourceSetService';
import type { SourceFile, SourceSet, SourceSetProgressionMode, SourceSetVisibility } from '../types/sourceSet';
import { createBackgroundJob } from '../services/gemini';

function formatBytes(bytes: number): string {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
}

function createLocalFileId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatDate(value: any): string {
  if (!value) return 'Not available';

  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    return date.toLocaleString();
  } catch {
    return 'Not available';
  }
}

export function SourceSetDetail() {
  const { sourceSetId } = useParams();
  const navigate = useNavigate();
  const { user, memberships, currentOrg } = useAuth();

  const [sourceSet, setSourceSet] = useState<SourceSet | null>(null);
  const [files, setFiles] = useState<SourceFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [addingUrl, setAddingUrl] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [visibility, setVisibility] = useState<SourceSetVisibility>('private');
  const [progressionMode, setProgressionMode] = useState<SourceSetProgressionMode>('flexible');
  const [targetOrgId, setTargetOrgId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [generatingLearning, setGeneratingLearning] = useState(false);
  const [activeJob, setActiveJob] = useState<any | null>(null);
  const [redirectJobId, setRedirectJobId] = useState<string | null>(null);

  const latestGenerationJobId = sourceSet?.generationJobIds?.length
    ? sourceSet.generationJobIds[sourceSet.generationJobIds.length - 1]
    : null;

  const latestBriefId = sourceSet?.documentBriefId || activeJob?.briefId || null;

  const loadSourceSet = useCallback(async () => {
    if (!sourceSetId || !user?.uid) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await getSourceSet(sourceSetId);

      if (!data) {
        setError('Source Set was not found.');
        setSourceSet(null);
        setFiles([]);
        return;
      }

      if (data.ownerId !== user.uid) {
        setError('You do not have access to this Source Set.');
        setSourceSet(null);
        setFiles([]);
        return;
      }

      const fileData = await listSourceSetFiles(sourceSetId);
      setSourceSet(data);
      setFiles(fileData);
      setVisibility(data.visibility || 'private');
      setProgressionMode(data.progressionMode || 'flexible');
      setTargetOrgId(data.organisationId || currentOrg?.id || memberships?.[0]?.orgId || '');
    } catch (err: any) {
      console.error('Failed to load Source Set:', err);
      setError(err?.message || 'Failed to load Source Set.');
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id, memberships, sourceSetId, user?.uid]);

  useEffect(() => {
    loadSourceSet();
  }, [loadSourceSet]);

  // Subscribe to the latest Source Set generation job so tutors can track progress here.
  useEffect(() => {
    if (!latestGenerationJobId || !user?.uid) {
      setActiveJob(null);
      return;
    }

    const jobRef = doc(db, 'jobs', latestGenerationJobId);

    const unsubscribe = onSnapshot(jobRef, async (snapshot) => {
      if (!snapshot.exists()) {
        return;
      }

      const job = { id: snapshot.id, ...snapshot.data() } as any;
      setActiveJob(job);

      if (job.status === 'failed' || job.status === 'timed_out') {
        setGeneratingLearning(false);
        setGeneratingSummary(false);
        setError(job.errorMessage || job.message || 'Source Set processing failed.');
      }

      if ((job.status === 'completed' || job.status === 'review_ready') && job.briefId) {
        setGeneratingSummary(false);
        if (redirectJobId === job.id) {
          setRedirectJobId(null);
          setSuccess('Summary ready. Opening the summary page...');
          navigate(`/source-sets/${sourceSetId}/summary?briefId=${job.briefId}`);
        } else {
          setSuccess('A generated summary is available. Use View Latest Summary to open it.');
        }
      }

      if (job.status === 'review_ready' && job.intent === 'course' && job.draftId) {
        setGeneratingLearning(false);
        if (redirectJobId === job.id) {
          setRedirectJobId(null);
          setSuccess('Learning draft ready. Opening course review...');
          navigate(`/courses/manage?tab=create&sourceSetJobId=${job.id}&sourceSetId=${sourceSetId}`);
        } else {
          setSuccess('A learning draft is ready. Open Create Learning review from My Courses.');
        }
      }
    }, (jobError: any) => {
      console.error('Failed to subscribe to Source Set job:', jobError);
      setError(jobError?.message || 'Could not track summary generation progress.');
    });

    return () => unsubscribe();
  }, [latestGenerationJobId, redirectJobId, navigate, sourceSetId, user?.uid]);

  const handleUpload = async () => {
    if (!user?.uid || !sourceSetId || !sourceSet) {
      setError('Source Set and logged-in user are required.');
      return;
    }

    const filesToUpload = selectedFiles ? Array.from(selectedFiles) : [];

    const validation = validateSourceSetFiles(
      filesToUpload,
      sourceSet.fileCount || 0,
      sourceSet.totalBytes || 0
    );

    if (!validation.valid) {
      setError(validation.errors.join(' '));
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      for (const file of filesToUpload) {
        const localFileId = createLocalFileId();
        const fileType = getSourceFileType(file.name, file.type);
        const storagePath = buildSourceSetStoragePath({
          userId: user.uid,
          sourceSetId,
          fileId: localFileId,
          fileName: file.name,
          organisationId: sourceSet.organisationId || null
        });

        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);

        await addSourceFileMetadata({
          sourceSetId,
          ownerId: user.uid,
          organisationId: sourceSet.organisationId || null,
          originalFileName: file.name,
          storagePath,
          fileType,
          mimeType: file.type,
          fileSizeBytes: file.size,
          pageCount: null
        });
      }

      setSelectedFiles(null);
      setSuccess('File upload completed. Source Set metadata has been updated.');
      await loadSourceSet();
    } catch (err: any) {
      console.error('Failed to upload Source Set files:', err);
      setError(err?.message || 'Failed to upload files.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-9 h-9 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-text-alt">Loading Source Set...</p>
        </div>
      </div>
    );
  }

  if (!sourceSet) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main px-4 py-8">
        <div className="max-w-3xl mx-auto bg-bg-alt border border-border-main rounded-3xl p-8">
          <AlertTriangle className="w-10 h-10 text-rose-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Source Set unavailable</h1>
          <p className="text-text-alt mb-6">{error || 'The requested Source Set could not be loaded.'}</p>
          <Link to="/source-sets" className="inline-flex items-center gap-2 text-primary font-semibold">
            <ArrowLeft className="w-4 h-4" />
            Back to Source Sets
          </Link>
        </div>
      </div>
    );
  }

  const handleAddUrl = async () => {
    if (!user?.uid || !sourceSetId || !sourceSet) {
      setError('Source Set and logged-in user are required.');
      return;
    }

    const validation = validateSourceSetUrl(sourceUrl, files);

    if (!validation.valid) {
      setError(validation.errors.join(' '));
      return;
    }

    setAddingUrl(true);
    setError('');
    setSuccess('');

    try {
      await addSourceUrlMetadata({
        sourceSetId,
        ownerId: user.uid,
        organisationId: sourceSet.organisationId || null,
        url: sourceUrl
      });

      setSourceUrl('');
      setSuccess('Webpage URL added to this Source Set.');
      await loadSourceSet();
    } catch (err: any) {
      console.error('Failed to add Source Set URL:', err);
      setError(err?.message || 'Failed to add webpage URL.');
    } finally {
      setAddingUrl(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!sourceSetId || !sourceSet) {
      setError('Source Set is required before settings can be saved.');
      return;
    }

    if (visibility === 'organisation' && !targetOrgId) {
      setError('Please select an organisation before saving organisation visibility.');
      return;
    }

    setSavingSettings(true);
    setError('');
    setSuccess('');

    try {
      await updateSourceSetSettings(sourceSetId, {
        visibility,
        progressionMode,
        organisationId: visibility === 'organisation' ? targetOrgId : null
      });

      setSourceSet((previous) => previous ? {
        ...previous,
        visibility,
        progressionMode,
        organisationId: visibility === 'organisation' ? targetOrgId : null
      } : previous);

      setSuccess('Source Set settings saved.');
    } catch (err: any) {
      console.error('Failed to save Source Set settings:', err);
      setError(err?.message || 'Failed to save Source Set settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const buildProcessingSources = (sourceFiles: SourceFile[]) => {
    return sourceFiles.map((file) => {
      if (file.fileType === 'url' || file.sourceKind === 'url') {
        return {
          id: file.fileId,
          name: file.title || file.originalFileName || file.url || 'Webpage',
          type: 'url',
          url: file.url,
          sourceId: file.fileId,
          sourceSetId: file.sourceSetId
        };
      }

      return {
        id: file.fileId,
        name: file.originalFileName,
        type: file.fileType === 'docx' ? 'word' : 'pdf',
        data: '',
        storagePath: file.storagePath,
        sourceId: file.fileId,
        sourceSetId: file.sourceSetId
      };
    });
  };

  const handleCreateLearning = async () => {
    if (!user?.uid || !sourceSetId || !sourceSet) {
      setError('Source Set and logged-in user are required.');
      return;
    }

    if (files.length === 0) {
      setError('Add at least one source before creating learning.');
      return;
    }

    const unsupportedFiles = files.filter((file) => !['pdf', 'docx', 'url'].includes(file.fileType));
    if (unsupportedFiles.length > 0) {
      setError('Create Learning currently supports PDF, DOCX, and webpage URLs. PPTX course generation support will be connected later.');
      return;
    }

    if (visibility === 'organisation' && !targetOrgId) {
      setError('Please select an organisation before creating organisation learning.');
      return;
    }

    setGeneratingLearning(true);
    setError('');
    setSuccess('');

    try {
      await updateSourceSetSettings(sourceSetId, {
        visibility,
        progressionMode,
        organisationId: visibility === 'organisation' ? targetOrgId : null
      });

      const sources = buildProcessingSources(files);

      const { jobId } = await createBackgroundJob(
        sourceSet.title,
        sources,
        user.uid,
        undefined,
        'course',
        undefined,
        {
          description: sourceSet.description || '',
          visibility,
          progressionMode,
          sourceSetId,
          organisationId: visibility === 'organisation' ? targetOrgId : null
        }
      );

      await attachGenerationJobToSourceSet(sourceSetId, jobId);
      await updateSourceSetStatus(sourceSetId, 'processing', 'generating_course');
      setRedirectJobId(jobId);

      setSourceSet((previous) => previous ? {
        ...previous,
        status: 'processing',
        processingStatus: 'generating_course',
        visibility,
        progressionMode,
        organisationId: visibility === 'organisation' ? targetOrgId : null,
        generationJobIds: [...(previous.generationJobIds || []), jobId]
      } : previous);

      setSuccess(`Create Learning started. Job ID: ${jobId}. Progress will appear on this page.`);
    } catch (err: any) {
      console.error('Failed to create learning from Source Set:', err);
      setError(getFriendlyErrorMessage(err, 'create_learning', 'Failed to start Create Learning.'));
      setGeneratingLearning(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!user?.uid || !sourceSet) {
      setError('You must be logged in to generate a summary.');
      return;
    }

    if (files.length === 0) {
      setError('Please upload at least one file before generating a summary.');
      return;
    }

    const unsupportedFiles = files.filter((file) => !['pdf', 'docx', 'url'].includes(file.fileType));
    if (unsupportedFiles.length > 0) {
      setError('Summary generation currently supports PDF, DOCX, and webpage URLs. PPTX summary support will be connected later.');
      return;
    }

    setGeneratingSummary(true);
    setError('');
    setSuccess('');

    try {
      const sources = buildProcessingSources(files);
      const token = await user.getIdToken();
      const organisationId = visibility === 'organisation' ? (targetOrgId || sourceSet.organisationId || '') : '';

      const response = await fetch('/api/process-documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(organisationId ? { 'X-Organization-Id': organisationId } : {})
        },
        body: JSON.stringify({
          title: sourceSet.title,
          sources,
          userId: user.uid,
          intent: 'brief',
          sourceSetId: sourceSet.sourceSetId
        })
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.success) {
        throw createApiError(response, payload, 'Failed to start summary generation.');
      }

      const jobId = payload?.data?.jobId || payload?.jobId;
      if (!jobId) {
        throw new Error('Summary generation started but no job ID was returned.');
      }

      setRedirectJobId(jobId);

      await attachGenerationJobToSourceSet(sourceSet.sourceSetId, jobId);
      await updateSourceSetStatus(sourceSet.sourceSetId, 'processing', 'generating_brief', null);

      setSourceSet((previous) => previous ? {
        ...previous,
        status: 'processing',
        processingStatus: 'generating_brief',
        generationJobIds: [...(previous.generationJobIds || []), jobId]
      } : previous);

      setSuccess(`Summary generation started. Job ID: ${jobId}. Progress will appear on this page.`);
    } catch (err: any) {
      console.error('Failed to generate Source Set summary:', err);
      setError(getFriendlyErrorMessage(err, 'source_summary', 'Failed to generate summary.'));
    } finally {
      setGeneratingSummary(false);
    }
  };


  return (
    <div className="min-h-screen bg-bg-main text-text-main px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <Link to="/source-sets" className="inline-flex items-center gap-2 text-text-alt hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Source Sets
        </Link>

        <div className="bg-bg-alt border border-border-main rounded-3xl p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <p className="text-sm font-semibold text-primary uppercase tracking-wide">Source Set</p>
              <h1 className="text-3xl font-bold mt-2">{sourceSet.title}</h1>
              <p className="text-text-alt mt-3 max-w-3xl">
                {sourceSet.description || 'No description provided.'}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-xs px-3 py-1 rounded-full bg-bg-main border border-border-main">
                Status: {sourceSet.status}
              </span>
              <span className="text-xs px-3 py-1 rounded-full bg-bg-main border border-border-main">
                {sourceSet.fileCount || 0} file(s)
              </span>
              <span className="text-xs px-3 py-1 rounded-full bg-bg-main border border-border-main">
                {formatBytes(sourceSet.totalBytes || 0)}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-400 whitespace-pre-line">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-400">
            {success}
          </div>
        )}

        {activeJob && (
          <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/10 p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <div>
                <h2 className="font-bold text-text-main">Source Set Processing Progress</h2>
                <p className="text-sm text-text-alt">{activeJob.message || 'Processing Source Set summary...'}</p>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-bg-main border border-border-main uppercase font-bold text-primary">
                {activeJob.status || 'queued'}
              </span>
            </div>
            <div className="w-full h-3 rounded-full bg-bg-main border border-border-main overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${Math.max(0, Math.min(100, Number(activeJob.progress || 0)))}%` }}
              />
            </div>
            <div className="mt-2 flex flex-col md:flex-row md:items-center md:justify-between gap-1 text-xs text-text-alt">
              <span>{activeJob.currentStep || 'queued'}</span>
              <span>{Number(activeJob.progress || 0)}%</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-bg-alt border border-border-main rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-bold text-xl">Upload Files</h2>
                <p className="text-sm text-text-alt">Add files to this Source Set.</p>
              </div>
            </div>

            <input
              type="file"
              multiple
              accept=".pdf,.docx,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              onChange={(event) => setSelectedFiles(event.target.files)}
              className="w-full mb-4 text-sm text-text-alt file:mr-4 file:py-3 file:px-4 file:rounded-xl file:border-0 file:bg-primary file:text-white file:font-semibold hover:file:opacity-90"
            />

            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFiles || selectedFiles.length === 0}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-primary text-white font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              {uploading ? 'Uploading...' : 'Upload Selected Files'}
            </button>

            <div className="mt-5 p-4 rounded-2xl bg-bg-main border border-border-main">
              <h3 className="font-semibold text-sm mb-2">V1 limits</h3>
              <ul className="text-xs text-text-alt space-y-1">
                <li>Maximum sources: 5</li>
                <li>Maximum URLs: 3</li>
                <li>Maximum file size: 25MB</li>
                <li>Maximum total file size: 50MB</li>
                <li>Supported: PDF, DOCX, URL. PPTX upload is stored but AI summary support comes later.</li>
              </ul>
            </div>

            <div className="mt-6 border-t border-border-main pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <LinkIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-bold text-lg">Add Webpage / URL</h2>
                  <p className="text-sm text-text-alt">Add one exact webpage, article, or internal URL.</p>
                </div>
              </div>

              <input
                type="url"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder="https://example.com/article"
                className="w-full mb-3 px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main placeholder-text-alt/40 focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />

              <button
                type="button"
                onClick={handleAddUrl}
                disabled={addingUrl || !sourceUrl.trim()}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl border border-primary/30 bg-primary/10 text-primary font-bold hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {addingUrl ? <Loader2 className="w-5 h-5 animate-spin" /> : <Globe className="w-5 h-5" />}
                {addingUrl ? 'Adding URL...' : 'Add URL Source'}
              </button>
            </div>

            <div className="mt-6 border-t border-border-main pt-6 space-y-5">
              <div>
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  Visibility & Sharing
                </h2>
                <p className="text-sm text-text-alt mt-1">Choose who should access learning created from this Source Set.</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => setVisibility('private')}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    visibility === 'private'
                      ? 'bg-primary/10 border-primary ring-2 ring-primary/20'
                      : 'bg-bg-main border-border-main hover:border-text-alt/30'
                  }`}
                >
                  <Lock className="w-5 h-5 text-text-alt mb-2" />
                  <span className="block font-bold text-sm">Keep Private</span>
                  <span className="block text-xs text-text-alt mt-1">Only you can access this Source Set output.</span>
                </button>

                <button
                  type="button"
                  disabled={!memberships || memberships.length === 0}
                  onClick={() => {
                    setVisibility('organisation');
                    if (!targetOrgId && memberships && memberships.length > 0) {
                      setTargetOrgId(memberships[0].orgId);
                    }
                  }}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    visibility === 'organisation'
                      ? 'bg-primary/10 border-primary ring-2 ring-primary/20'
                      : 'bg-bg-main border-border-main hover:border-text-alt/30'
                  } ${!memberships || memberships.length === 0 ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                >
                  <Shapes className={`w-5 h-5 mb-2 ${visibility === 'organisation' ? 'text-primary' : 'text-text-alt'}`} />
                  <span className="block font-bold text-sm">Share with Organisation</span>
                  <span className="block text-xs text-text-alt mt-1">
                    {!memberships || memberships.length === 0
                      ? 'Join an organisation to enable this.'
                      : 'Only selected organisation members can access this output.'}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setVisibility('public')}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    visibility === 'public'
                      ? 'bg-primary/10 border-primary ring-2 ring-primary/20'
                      : 'bg-bg-main border-border-main hover:border-text-alt/30'
                  }`}
                >
                  <Globe className="w-5 h-5 text-text-alt mb-2" />
                  <span className="block font-bold text-sm">Publish Publicly</span>
                  <span className="block text-xs text-text-alt mt-1">Learning created from this Source Set can appear publicly after review.</span>
                </button>
              </div>

              {visibility === 'organisation' && memberships && memberships.length > 0 && (
                <select
                  value={targetOrgId}
                  onChange={(event) => setTargetOrgId(event.target.value)}
                  className="w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                >
                  {memberships.map((membership: any) => (
                    <option key={membership.orgId} value={membership.orgId}>Organisation: {membership.orgId}</option>
                  ))}
                </select>
              )}

              <div>
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-primary" />
                  Course Progression
                </h2>
                <p className="text-sm text-text-alt mt-1">Choose how learners move through learning created from this Source Set.</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => setProgressionMode('flexible')}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    progressionMode === 'flexible'
                      ? 'bg-primary/10 border-primary ring-2 ring-primary/20'
                      : 'bg-bg-main border-border-main hover:border-text-alt/30'
                  }`}
                >
                  <Zap className={`w-5 h-5 mb-2 ${progressionMode === 'flexible' ? 'text-primary' : 'text-text-alt'}`} />
                  <span className="block font-bold text-sm">Flexible</span>
                  <span className="block text-xs text-text-alt mt-1">Learners can move freely. Knowledge checks are recommended but not required.</span>
                </button>

                <button
                  type="button"
                  onClick={() => setProgressionMode('locked')}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    progressionMode === 'locked'
                      ? 'bg-primary/10 border-primary ring-2 ring-primary/20'
                      : 'bg-bg-main border-border-main hover:border-text-alt/30'
                  }`}
                >
                  <Lock className={`w-5 h-5 mb-2 ${progressionMode === 'locked' ? 'text-primary' : 'text-text-alt'}`} />
                  <span className="block font-bold text-sm">Locked</span>
                  <span className="block text-xs text-text-alt mt-1">Learners must complete and pass required knowledge checks before moving forward.</span>
                </button>
              </div>

              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-bg-main border border-border-main text-text-main font-bold hover:bg-bg-alt disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {savingSettings ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                {savingSettings ? 'Saving Settings...' : 'Save Source Set Settings'}
              </button>
            </div>

          </div>

          <div className="lg:col-span-2 bg-bg-alt border border-border-main rounded-3xl p-6">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-bold text-xl">Sources in this Source Set</h2>
                <p className="text-sm text-text-alt">Uploaded files, webpages, and metadata.</p>
              </div>

              <button
                onClick={loadSourceSet}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border-main hover:bg-bg-main transition-colors"
              >
                <RefreshCcw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {files.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-border-main rounded-3xl">
                <FileText className="w-12 h-12 mx-auto mb-4 text-text-alt" />
                <h3 className="text-lg font-bold mb-2">No sources added yet</h3>
                <p className="text-text-alt">Upload files or add URLs to start building this Source Set.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {files.map((file) => (
                  <div
                    key={file.fileId}
                    className="p-4 rounded-2xl bg-bg-main border border-border-main flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{file.title || file.originalFileName}</h3>
                        <p className="text-xs text-text-alt mt-1 break-all">
                          {file.sourceKind === 'url' || file.fileType === 'url' ? file.url : file.storagePath}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 md:justify-end">
                      <span className="text-xs px-3 py-1 rounded-full bg-bg-alt border border-border-main">
                        {file.sourceKind === 'url' || file.fileType === 'url' ? 'URL' : file.fileType.toUpperCase()}
                      </span>
                      <span className="text-xs px-3 py-1 rounded-full bg-bg-alt border border-border-main">
                        {file.sourceKind === 'url' || file.fileType === 'url' ? 'Webpage' : formatBytes(file.fileSizeBytes)}
                      </span>
                      <span className="text-xs px-3 py-1 rounded-full bg-bg-alt border border-border-main">
                        {file.processingStatus}
                      </span>
                      <span className="text-xs px-3 py-1 rounded-full bg-bg-alt border border-border-main">
                        {formatDate(file.createdAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {latestBriefId && (
              <div className="mt-6 mb-2">
                <Link
                  to={`/source-sets/${sourceSet.sourceSetId}/summary?briefId=${latestBriefId}`}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-bold hover:bg-emerald-500/20 transition-colors"
                >
                  View Latest Summary
                </Link>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={handleGenerateSummary}
                disabled={generatingSummary || files.length === 0}
                className="px-4 py-4 rounded-2xl bg-primary text-white font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                {generatingSummary ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting Summary...
                  </span>
                ) : (
                  'Generate Summary'
                )}
                <span className="block text-xs mt-1 font-normal">
                  {files.length === 0 ? 'Upload files first' : 'Uses uploaded files'}
                </span>
              </button>

              <button
                type="button"
                onClick={handleCreateLearning}
                disabled={generatingLearning || files.length === 0}
                className="px-4 py-4 rounded-2xl bg-bg-main border border-border-main text-text-main font-bold hover:bg-bg-alt disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingLearning ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Learning...
                  </span>
                ) : (
                  'Create Learning'
                )}
                <span className="block text-xs mt-1 font-normal">
                  {files.length === 0 ? 'Add sources first' : 'Creates a reviewable draft'}
                </span>
              </button>

              {files.length === 0 ? (
                <button
                  disabled
                  className="px-4 py-4 rounded-2xl bg-bg-main border border-border-main text-text-alt cursor-not-allowed"
                >
                  Ask Source
                  <span className="block text-xs mt-1">Add sources first</span>
                </button>
              ) : (
                <Link
                  to={`/source-sets/${sourceSet.sourceSetId}/ask`}
                  className="px-4 py-4 rounded-2xl bg-bg-main border border-border-main text-text-main font-bold hover:bg-bg-alt transition-colors text-center"
                >
                  Ask Source
                  <span className="block text-xs mt-1 font-normal">Ask this Source Set</span>
                </Link>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
